import React, { useState, Fragment } from 'react';

interface IAutoCompleteProps {
    suggestions: string[];
    filterName: (name: string) => void;
    clickOnCompletion: (name: string) => void;
    search: string;
    userInput: string;
}

export function AutoComplete({
    suggestions,
    filterName,
    clickOnCompletion,
    search,
    userInput: externalInput,
}: IAutoCompleteProps) {
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>(
        []
    );
    const [activeSuggestion, setActiveSuggestion] = useState<number | null>(
        null
    );
    const [showSuggestions, setShowSuggestions] = useState(false);

    const normalizeText = (value: string) =>
        value
            .toUpperCase()
            .normalize('NFD')
            .replace(/(?:[\u0300-\u036f]|\W)/g, '');

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.currentTarget.value;
        const cleanedSearch = normalizeText(rawValue);

        const filtered = suggestions.filter((s) =>
            normalizeText(s).includes(cleanedSearch)
        );

        setFilteredSuggestions(filtered);
        setActiveSuggestion(null);
        setShowSuggestions(true);

        filterName(rawValue);
    };

    const onClick = (text: string) => {
        setFilteredSuggestions([]);
        setShowSuggestions(false);
        clickOnCompletion(text);
        filterName(text);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (activeSuggestion !== null) {
                const selected = filteredSuggestions[activeSuggestion];
                setShowSuggestions(false);
                filterName(selected);
            } else {
                setShowSuggestions(false);
            }
        } else if (e.key === 'ArrowUp') {
            if (activeSuggestion !== null && activeSuggestion > 0) {
                setActiveSuggestion(activeSuggestion - 1);
            }
        } else if (e.key === 'ArrowDown') {
            if (activeSuggestion === null && filteredSuggestions.length > 0) {
                setActiveSuggestion(0);
            } else if (
                activeSuggestion !== null &&
                activeSuggestion < filteredSuggestions.length - 1
            ) {
                setActiveSuggestion(activeSuggestion + 1);
            }
        }
    };

    const clearUserInput = () => {
        setFilteredSuggestions([]);
        setShowSuggestions(false);
        filterName('');
    };

    const value = externalInput ?? '';

    return (
        <Fragment>
            <div className="autocomplete__container">
                <label className="sr-only sr-only-focusable">{search}</label>

                <input
                    title={search}
                    type="text"
                    name="search"
                    placeholder={search}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    className="autocomplete__input"
                />

                <button
                    type="button"
                    className="autocomplete__clear"
                    onClick={clearUserInput}
                >
                    <i className="icon-cross" /> Clear
                </button>
            </div>

            {showSuggestions && value && filteredSuggestions.length > 0 && (
                <ul className="autocomplete__list" id="autocomplete">
                    {filteredSuggestions.map((suggestion, index) => (
                        <li key={index} id={index.toString()}>
                            <button
                                type="button"
                                onClick={() => onClick(suggestion)}
                                className={
                                    index === activeSuggestion
                                        ? 'state-focus'
                                        : ''
                                }
                            >
                                {suggestion}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Fragment>
    );
}
